import { SyntheticEvent, useEffect, useState } from 'react';
import { shuffle } from 'lodash';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Zindexes } from '@zapier/theme';
import {
  Button,
  Colors,
  Icon,
  Surface,
  Text,
  Typography,
} from '@zapier/design-system';
import { useScreenSize } from '../../hooks/useScreenSize';

import { Options } from './Options';
import { mediaQueries } from '../../constants';

type OptionsShape = {
  id: string;
  label: string;
};

export type Props = {
  /**
   * Dismiss modal when user clicks on the `X`
   */
  dismissPopup: () => void;
  /**
   * Dismiss modal when user clicks on the `Close` button
   */
  dismissPopupOnClose: () => void;
  /**
   * Used to track an event when the popup loads.
   */
  onMount: () => void;
  onSubmit: (selection: string) => void;
  surveySentSuccessfully: boolean;
};

export const shuffledOptions: Array<OptionsShape> = shuffle([
  {
    id: 'search-engine',
    label: 'Search engine',
  },
  {
    id: 'facebook-instagram',
    label: 'Facebook/Instagram',
  },
  {
    id: 'website-referral',
    label: 'Website Referrals',
  },
  {
    id: 'podcast',
    label: 'Podcast',
  },
  {
    id: 'word-of-mouth',
    label: 'Word of mouth',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
  },
  {
    id: 'youtube',
    label: 'YouTube',
  },
  {
    id: 'billboard',
    label: 'Billboard',
  },
]);

const Survey = ({
  dismissPopup,
  dismissPopupOnClose,
  onSubmit,
  onMount,
  surveySentSuccessfully,
}: Props) => {
  const { isMobile } = useScreenSize();

  const [selectedOption, setSelectedOption] = useState('');

  // Begin as collapsed for small only.
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  // Once expanded, it may not be collapsed again.
  const expandSurvey = () => {
    setIsCollapsed(false);
  };

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const handleSelectOption = (e: SyntheticEvent) => {
    const value = (e.target as HTMLInputElement).value;
    setSelectedOption(value);
  };

  function handleSubmit(e: SyntheticEvent) {
    e.preventDefault();
    onSubmit(selectedOption);
  }

  function renderForm() {
    return (
      <Form onSubmit={handleSubmit}>
        {surveySentSuccessfully ? (
          <SuccessWrapper>
            <Icon color="success" name="formCheckCircle" size={60} />
            <Text tag="h2" textAlign="center" type="pageHeader5">
              Thank you
            </Text>
          </SuccessWrapper>
        ) : (
          <ControlsWrapper>
            <Options
              onChange={handleSelectOption}
              options={[
                ...shuffledOptions,
                {
                  id: 'other',
                  label: 'Other',
                },
              ]}
            />
          </ControlsWrapper>
        )}
        <SubmitWrapper>
          {surveySentSuccessfully ? (
            <Button
              isFullWidth={true}
              onClick={dismissPopupOnClose}
              size="small"
              type="button"
            >
              Close
            </Button>
          ) : (
            <Button disabled={!selectedOption} isFullWidth={true} size="small">
              Submit
            </Button>
          )}
        </SubmitWrapper>
      </Form>
    );
  }

  const root = (
    <Surface backgroundColor="white" borderColor="gray20">
      <Content isCollapsed={isCollapsed}>
        <Header>
          <CloseButton onClick={dismissPopup}>
            <Icon
              ariaLabel="Close the survey"
              color="gray60"
              name="formX"
              size={25}
            />
          </CloseButton>
          {!surveySentSuccessfully && (
            <Text fontWeight={700} tag="h2" type="paragraph1Short">
              What brought you to us today?
            </Text>
          )}
        </Header>
        {isCollapsed ? (
          <ExpandButton onClick={expandSurvey}>(tap to answer)</ExpandButton>
        ) : (
          renderForm()
        )}
      </Content>
    </Surface>
  );

  return <Container>{root}</Container>;
};

export { Survey };

const Container = styled.div`
  bottom: -8px;
  border-radius: 10px;
  box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.1);
  position: fixed;
  right: 50px;
  z-index: ${Zindexes.fixedSurvey};

  @media screen and (max-width: 565px) {
    right: 50%;
    transform: translateX(50%);
  },
`;

const Content = styled.div<{
  isCollapsed: boolean;
}>(({ isCollapsed }) => [
  css`
    position: relative;
    padding: 25px 15px 0;
    max-width: 95vw;
    width: 330px;
    padding-bottom: ${isCollapsed ? '25px' : 0};

    ${mediaQueries.tablet} {
      padding-bottom: 0;
    }
  `,
]);

const Header = styled.div`
  padding-top: 10px;
`;

const CloseButton = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const ExpandButton = styled.button`
  ${Typography.smallPrint1};
  color: ${Colors.blueJeans};
  margin-top: 5px;
  background-color: transparent;
  outline: none;
  border: none;
`;

const Form = styled.form`
  margin-top: 30px;
`;

const SuccessWrapper = styled.div`
  align-items: initial;
  display: grid;
  justify-items: center;
  min-height: 190px;
`;

const ControlsWrapper = styled.div`
  padding-bottom: 20px;
`;

const SubmitWrapper = styled.div`
  background-color: ${Colors.gray5};
  border-top: 1px solid ${Colors.gray20};
  margin-left: -15px;
  margin-right: -15px;
  padding: 20px 15px 33px 15px;
`;
